import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, ExternalLink, Users, Star, Radio } from "lucide-react";

const ROLE_STYLE = {
  employee: { label: "TEAM", color: "text-white", border: "border-[var(--ff-border)]" },
  host: { label: "HOST", color: "text-[#FFD60A]", border: "border-[#FFD60A]" },
};

export default function Team() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team");
        setTeam(data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const hosts = team.filter((m) => m.roles.includes("host"));
  const staff = team.filter((m) => !m.roles.includes("host"));

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24" data-testid="team-page">
      <div className="mb-14 max-w-3xl">
        <div className="label-eyebrow mb-3 flex items-center gap-2 text-[var(--ff-red)]">
          <Users size={14} /> ▮ NETWORK / TEAM
        </div>
        <h1 className="font-display text-5xl lg:text-7xl leading-none">
          THE PEOPLE<br />BEHIND THE NETWORK.
        </h1>
        <p className="text-[var(--ff-text-dim)] text-base lg:text-lg mt-6 max-w-2xl">
          Everyone currently carrying the Employee role in our Discord — producers, hosts, editors, ops. Team members join by getting the role in our server; the roster updates live.
        </p>
      </div>

      {loading ? (
        <div className="font-mono-ui text-sm text-[var(--ff-text-dim)]">Loading team…</div>
      ) : team.length === 0 ? (
        <div className="surface-card p-16 text-center" data-testid="team-empty">
          <Users size={40} className="mx-auto text-[var(--ff-text-dim)] mb-6" />
          <div className="font-display text-3xl mb-3">TEAM ROSTER COMING SOON.</div>
          <p className="text-[var(--ff-text-dim)] text-sm max-w-md mx-auto">
            No team members have signed in with Discord yet. Team members appear here automatically once they log in with the Employee role.
          </p>
        </div>
      ) : (
        <>
          {hosts.length > 0 && (
            <section className="mb-16" data-testid="team-hosts-section">
              <div className="flex items-center justify-between mb-6">
                <div className="label-eyebrow flex items-center gap-2 text-[#FFD60A]"><Star size={12} /> ▮ ON CAMERA · {hosts.length}</div>
                <Link to="/hosts" className="btn-ghost text-xs" data-testid="team-hosts-link">
                  All Hosts <ArrowRight size={12} />
                </Link>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hosts.map((m) => <TeamCard key={m.discord_id} m={m} accent="host" />)}
              </div>
            </section>
          )}

          {staff.length > 0 && (
            <section data-testid="team-staff-section">
              <div className="flex items-center justify-between mb-6">
                <div className="label-eyebrow flex items-center gap-2"><Radio size={12} /> ▮ BEHIND THE SCENES · {staff.length}</div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {staff.map((m) => <TeamCard key={m.discord_id} m={m} accent="employee" />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function TeamCard({ m, accent }) {
  const style = ROLE_STYLE[accent];
  const initial = (m.username || "?")[0].toUpperCase();
  const inner = (
    <div className={`surface-card p-6 flex flex-col gap-4 h-full transition-all ${accent === "host" ? "hover:border-[#FFD60A]" : "hover:border-white"}`} data-testid={`team-card-${m.discord_id}`}>
      <div className="flex items-center gap-4">
        {m.avatar ? (
          <img src={m.avatar} alt={m.username} className={`w-16 h-16 object-cover ring-2 ${accent === "host" ? "ring-[#FFD60A]" : "ring-[var(--ff-border)]"}`} />
        ) : (
          <div className={`w-16 h-16 flex items-center justify-center font-display text-2xl ${accent === "host" ? "bg-[#FFD60A] text-black" : "bg-[var(--ff-red)] text-white"}`}>
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl leading-tight truncate">{m.username}</div>
          {m.title && (
            <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] uppercase tracking-[0.12em] mt-1">{m.title}</div>
          )}
          <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 border font-mono-ui text-[0.55rem] uppercase tracking-[0.15em] ${style.border} ${style.color}`}>
            {m.roles.includes("host") && <Star size={9} fill="currentColor" />}
            {m.roles.map((r) => ROLE_STYLE[r]?.label || r).join(" · ")}
          </div>
        </div>
      </div>
      {m.bio && <p className="text-xs text-[var(--ff-text-dim)] line-clamp-3">{m.bio}</p>}
      {m.social_url && (
        <a href={m.social_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[0.65rem] text-[var(--ff-red)] font-mono-ui uppercase tracking-[0.12em] hover:underline w-fit">
          <ExternalLink size={10} /> Social
        </a>
      )}
    </div>
  );
  return m.host_name
    ? <Link to={`/hosts/${encodeURIComponent(m.host_name)}`} className="block">{inner}</Link>
    : inner;
}
